// src/components/TransactionAlertsTable.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchTransactionsThunk } from '../features/transactions/transactionThunks';
import '../CSS/TransactionAlertsTable.css';
import ClipThumbnail from './ClipThumbnail';
import { clipThumbnailUrl } from '../utils/clipThumbnail';

const TransactionAlertsTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [videoOverlay, setVideoOverlay] = useState(null);

  // Safe defaults to avoid undefined errors
  const {
    transactions = [],
    loading = false,
    error = null,
    next = null,
    count = 0,
  } = useSelector((state) => state.transactions || {});

  // Fetch initial data
  useEffect(() => {
    if (transactions.length === 0) {
      dispatch(fetchTransactionsThunk({ offset: 0 }));
    }
  }, [dispatch, transactions.length]);


  // Show first 5 alerts
  const tableData = transactions.slice(0, 7);

  const handleRowClick = (id) => navigate(`/transaction/${id}`);
  
  const handleVideoClick = (e, videoUrl) => {
    e.stopPropagation();
    setVideoOverlay(videoUrl);
  };
  
  const closeVideoOverlay = () => setVideoOverlay(null);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Status badge component
  const StatusBadge = ({ flagged }) => {
    if (!flagged) return null;
    
    return (
      <span className="ta-status-badge">
        <span className="ta-status-dot"></span>
        Alert
      </span>
    );
  };

  // Empty state component
  const EmptyState = () => (
    <div className="ta-empty-state">
      <div className="ta-empty-icon">📊</div>
      <h3>No alerts to display</h3>
      <p>When transaction alerts occur, they will appear here</p>
    </div>
  );

  // Loading skeleton
  const SkeletonRow = () => (
    <tr className="ta-skeleton-row">
      <td><div className="ta-skeleton-pulse"></div></td>
      <td>
        <div className="ta-skeleton-pulse"></div>
        <div className="ta-skeleton-pulse ta-skeleton-short"></div>
      </td>
      <td><div className="ta-skeleton-pulse"></div></td>
      <td><div className="ta-skeleton-pulse"></div></td>
      <td><div className="ta-skeleton-pulse ta-skeleton-center"></div></td>
    </tr>
  );

  // Video thumbnail component
  const VideoThumbnail = ({ transaction }) => {
    if (!transaction.video_evidence) {
      return (
        <div className="ta-no-video">
          <div className="ta-no-video-content">
            <div className="ta-no-video-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z" />
              </svg>
            </div>
            <span className="ta-no-video-text">No Media</span>
          </div>
        </div>
      );
    }

    return (
      <div 
        className="ta-video-thumbnail"
        onClick={(e) => handleVideoClick(e, transaction.video_evidence)}
      >
        <ClipThumbnail videoUrl={transaction.video_evidence} />
        <div className="ta-play-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="ta-transaction-alerts-card">
      <div className="ta-card-header">
        <h2>Transaction Alerts</h2>
        <button
          className="ta-view-all-button"
          onClick={() => navigate('/suspicioustransactionalerts')}
        >
          View all
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
          </svg>
        </button>
      </div>

      <div className="ta-card-content">
        {loading ? (
          <table className="ta-transactions-table">
            <thead>
              <tr>
                <th className="ta-store-column">Store</th>
                <th className="ta-transaction-column">Transaction</th>
                <th className="ta-date-column">Date</th>
                <th className="ta-device-column">Device</th>
                <th className="ta-video-column">Video</th>
              </tr>
            </thead>
            <tbody>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </tbody>
          </table>
        ) : error ? (
          <div className="ta-error-container">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <p>{error}</p>
          </div>
        ) : tableData.length === 0 ? (
          <EmptyState />
        ) : (
          <table className="ta-transactions-table">
            <thead>
              <tr>
                <th className="ta-store-column">Store</th>
                <th className="ta-transaction-column">Transaction</th>
                <th className="ta-date-column">Date</th>
                <th className="ta-device-column">Device</th>
                <th className="ta-video-column">Video</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((transaction) => (
                <tr
                  key={transaction.id}
                  className={`ta-table-row ${transaction.flagged ? 'ta-flagged-row' : ''}`}
                  onClick={() => handleRowClick(transaction.id)}
                >
                  <td className="ta-store-cell">
                    <div className="ta-store-info">
                      {transaction.store?.image ? (
                        <img 
                          src={transaction.store.image} 
                          alt={transaction.store?.store_name || 'Store'} 
                          className="ta-store-image"
                        />
                      ) : (
                        <div className="ta-store-image-placeholder">
                          {(transaction.store?.store_name || 'S').charAt(0)}
                        </div>
                      )}
                      <div className="ta-store-details">
                        <div className="ta-store-name">{transaction.store?.store_name || '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="ta-transaction-cell">
                    <div className="ta-transaction-header">
                      <span className="ta-transaction-id">{transaction.transaction_id}</span>
                      <StatusBadge flagged={transaction.flagged} />
                    </div>
                    <div className="ta-transaction-description">{transaction.description}</div>
                  </td>
                  <td className="ta-date-cell">{formatDate(transaction.date)}</td>
                  <td className="ta-device-cell">
                    <div className="ta-device-info">
                      <span className="ta-device-name">{transaction.device || '—'}</span>
                    </div>
                  </td>
                  <td className="ta-video-cell">
                    <div className="ta-video-container">
                      <VideoThumbnail transaction={transaction} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {next && (
        <div className="ta-pagination-hint">
          <span>{transactions.length} of {count} alerts</span>
        </div>
      )}

      {/* Video Overlay */}
      {videoOverlay && (
        <div className="ta-video-overlay" onClick={closeVideoOverlay}>
          <div className="ta-video-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ta-close-button" onClick={closeVideoOverlay}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
            <video 
              className="ta-video-player" 
              src={videoOverlay} 
              poster={clipThumbnailUrl(videoOverlay) || undefined}
              controls 
              autoPlay
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionAlertsTable;